import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import {
  Plus,
  Note,
  ArrowsClockwise,
  FlagBanner,
  CalendarBlank,
  CheckCircle,
  ArrowCounterClockwise,
  Tag,
  FileText,
  PencilSimple,
  FloppyDisk,
  X
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

function TaskHistoryTimeline({ history, taskId, onEdited }) {
  const [editingId, setEditingId] = useState(null);
  const [editSummary, setEditSummary] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [saving, setSaving] = useState(false);

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'task_created':
        return <Plus size={16} weight="bold" className="text-navy" />;
      case 'note_added':
        return <Note size={16} weight="duotone" className="text-purple-600" />;
      case 'note_edited':
        return <PencilSimple size={16} weight="duotone" className="text-purple-400" />;
      case 'status_changed':
        return <ArrowsClockwise size={16} weight="duotone" className="text-navy" />;
      case 'priority_changed':
        return <FlagBanner size={16} weight="duotone" className="text-orange-600" />;
      case 'due_date_changed':
        return <CalendarBlank size={16} weight="duotone" className="text-green-600" />;
      case 'title_changed':
      case 'description_changed':
        return <PencilSimple size={16} weight="duotone" className="text-amber-600" />;
      case 'task_completed':
        return <CheckCircle size={16} weight="fill" className="text-emerald-600" />;
      case 'task_reopened':
        return <ArrowCounterClockwise size={16} weight="duotone" className="text-navy" />;
      case 'tags_changed':
        return <Tag size={16} weight="duotone" className="text-indigo-600" />;
      case 'subtask_added':
        return <FileText size={16} weight="duotone" className="text-teal-600" />;
      case 'subtask_updated':
        return <PencilSimple size={16} weight="duotone" className="text-teal-500" />;
      default:
        return <PencilSimple size={16} weight="duotone" className="text-slate-600" />;
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'task_created':
        return 'bg-blue-50 border-blue-200';
      case 'note_added':
        return 'bg-purple-50 border-purple-200';
      case 'note_edited':
        return 'bg-purple-50 border-purple-100';
      case 'status_changed':
        return 'bg-blue-50 border-blue-200';
      case 'priority_changed':
        return 'bg-orange-50 border-orange-200';
      case 'due_date_changed':
        return 'bg-green-50 border-green-200';
      case 'title_changed':
      case 'description_changed':
        return 'bg-amber-50 border-amber-200';
      case 'task_completed':
        return 'bg-emerald-50 border-emerald-200';
      case 'task_reopened':
        return 'bg-blue-50 border-blue-200';
      case 'tags_changed':
        return 'bg-indigo-50 border-indigo-200';
      case 'subtask_added':
        return 'bg-teal-50 border-teal-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const isEditable = (entry) => entry.action_type === 'note_added';

  const startEdit = (entry) => {
    setEditingId(entry.history_id);
    setEditSummary(entry.summary);
    setEditDetails(entry.details || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditSummary('');
    setEditDetails('');
  };

  const saveEdit = async (historyId) => {
    if (!editSummary.trim()) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/tasks/${taskId}/history/${historyId}`,
        { summary: editSummary.trim(), details: editDetails.trim() || null },
        { withCredentials: true }
      );
      setEditingId(null);
      if (onEdited) onEdited();
    } catch (err) {
      console.error('Failed to edit history entry:', err);
      alert('Failed to save edit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="history-timeline">
      {history.map((entry) => {
        const editing = editingId === entry.history_id;
        return (
          <div
            key={entry.history_id}
            className="relative pl-8 pb-4 border-l-2 border-slate-200 last:border-l-0 last:pb-0"
            data-testid={`history-entry-${entry.history_id}`}
          >
            {/* Timeline dot */}
            <div className={`absolute left-0 -translate-x-1/2 w-8 h-8 rounded-full border-2 flex items-center justify-center ${getActionColor(entry.action_type)}`}>
              {getActionIcon(entry.action_type)}
            </div>

            {/* Entry content */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  {editing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editSummary}
                        onChange={(e) => setEditSummary(e.target.value)}
                        autoFocus
                        className="w-full text-sm font-medium bg-white border border-blue-300 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-blue-100"
                        data-testid={`history-edit-summary-${entry.history_id}`}
                      />
                      <textarea
                        value={editDetails}
                        onChange={(e) => setEditDetails(e.target.value)}
                        placeholder="Details (optional)"
                        className="w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-blue-100 min-h-[60px] resize-none"
                        data-testid={`history-edit-details-${entry.history_id}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(entry.history_id)}
                          disabled={saving || !editSummary.trim()}
                          className="text-xs font-medium bg-navy text-white hover:bg-navy-hover px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                          data-testid={`history-save-edit-${entry.history_id}`}
                        >
                          <FloppyDisk size={12} weight="fill" />
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200"
                          data-testid={`history-cancel-edit-${entry.history_id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-slate-900 text-sm">{entry.summary}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {entry.user_name} · {formatTimestamp(entry.created_at)}
                        {entry.edited_at && (
                          <span className="ml-1.5 text-slate-400 italic" data-testid={`edited-indicator-${entry.history_id}`}>
                            · edited {formatTimestamp(entry.edited_at)}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
                {!editing && isEditable(entry) && taskId && (
                  <button
                    onClick={() => startEdit(entry)}
                    className="text-slate-300 hover:text-navy transition-colors p-1 flex-shrink-0 ml-2"
                    title="Edit this note"
                    data-testid={`history-edit-btn-${entry.history_id}`}
                  >
                    <PencilSimple size={14} />
                  </button>
                )}
              </div>

              {!editing && entry.details && (
                <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg p-2">
                  {entry.details}
                </p>
              )}

              {!editing && (entry.before_value || entry.after_value) && (
                <div className="mt-3 flex items-center gap-2 text-xs">
                  {entry.before_value && (
                    <span className="bg-red-50 text-red-700 px-2 py-1 rounded border border-red-200">
                      From: {entry.before_value}
                    </span>
                  )}
                  {entry.before_value && entry.after_value && (
                    <span className="text-slate-400">&rarr;</span>
                  )}
                  {entry.after_value && (
                    <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded border border-emerald-200">
                      To: {entry.after_value}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default TaskHistoryTimeline;
